import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  // Facebook,
  // Instagram,
  BarChart3,
  CheckCircle,
  XCircle,
  ExternalLink,
  RefreshCw,
  AlertCircle,
  Unlink,
} from "lucide-react";
// import MetaAPIService from "../../api/meta"; // META TEMPORARILY DISABLED
import GoogleAnalyticsService from "../../api/googleAnalytics";
import { useAuth, getAuthToken } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";

const SocialMediaConnect = ({ onConnect }) => {
  const { company } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const [connections, setConnections] = useState({
    // meta: { connected: false, loading: false, error: null }, // META TEMPORARILY DISABLED
    google: { connected: false, loading: false, error: null },
  });
  // const [metaData, setMetaData] = useState(null); // META TEMPORARILY DISABLED
  const [googleData, setGoogleData] = useState(null);

  useEffect(() => {
    checkExistingConnections();
  }, []);

  const checkExistingConnections = async () => {
    try {
      // META TEMPORARILY DISABLED
      // const metaStatus = await MetaAPIService.checkLoginStatus();
      // if (metaStatus.status === "connected") {
      //   setConnections((prev) => ({
      //     ...prev,
      //     meta: { connected: true, loading: false, error: null },
      //   }));
      //   await loadMetaData(metaStatus.authResponse.accessToken);
      // }

      const googleToken = localStorage.getItem("google_analytics_token");
      if (googleToken) {
        const isValid = await GoogleAnalyticsService.validateToken(googleToken);
        if (isValid) {
          setConnections((prev) => ({
            ...prev,
            google: { connected: true, loading: false, error: null },
          }));
          await loadGoogleData(googleToken);
        }
      }
    } catch (error) {
      console.error("Error checking connections:", error);
    }
  };

  /* META TEMPORARILY DISABLED
  const connectMeta = async () => {
    setConnections((prev) => ({
      ...prev,
      meta: { ...prev.meta, loading: true, error: null },
    }));

    try {
      const authResponse = await MetaAPIService.login();

      if (authResponse) {
        setConnections((prev) => ({
          ...prev,
          meta: { connected: true, loading: false, error: null },
        }));

        await loadMetaData(authResponse.accessToken);

        await saveConnection("meta", {
          accessToken: authResponse.accessToken,
          userId: authResponse.userID,
        });

        if (onConnect) onConnect("meta");
      }
    } catch (error) {
      console.error("Meta connection error:", error);
      let errorMessage =
        "Kon geen verbinding maken met Meta. Controleer uw instellingen.";

      if (error.message.includes("not configured")) {
        errorMessage =
          "Meta (Facebook/Instagram) is niet geconfigureerd. Neem contact op met de beheerder.";
      } else if (error.message.includes("FB.init")) {
        errorMessage =
          "Facebook SDK kon niet geïnitialiseerd worden. Controleer de configuratie.";
      }

      setConnections((prev) => ({
        ...prev,
        meta: {
          connected: false,
          loading: false,
          error: errorMessage,
        },
      }));
    }
  };
  */

  const connectGoogle = async () => {
    setConnections((prev) => ({
      ...prev,
      google: { ...prev.google, loading: true, error: null },
    }));

    try {
      const oauthUrl = GoogleAnalyticsService.getOAuthURL();
      window.location.href = oauthUrl;
    } catch (error) {
      console.error("Google connection error:", error);
      let errorMessage = "Kon geen verbinding maken met Google Analytics.";

      if (error.message.includes("not configured")) {
        errorMessage =
          "Google Analytics is niet geconfigureerd. Neem contact op met de beheerder.";
      }

      setConnections((prev) => ({
        ...prev,
        google: {
          connected: false,
          loading: false,
          error: errorMessage,
        },
      }));
    }
  };

  /* META TEMPORARILY DISABLED
  const disconnectMeta = async () => {
    try {
      await MetaAPIService.logout();
      setConnections((prev) => ({
        ...prev,
        meta: { connected: false, loading: false, error: null },
      }));
      setMetaData(null);
      await removeConnection("meta");
    } catch (error) {
      console.error("Meta disconnection error:", error);
    }
  };
  */

  const disconnectGoogle = async () => {
    try {
      localStorage.removeItem("google_analytics_token");
      setConnections((prev) => ({
        ...prev,
        google: { connected: false, loading: false, error: null },
      }));
      setGoogleData(null);
      await removeConnection("google");
    } catch (error) {
      console.error("Google disconnection error:", error);
    }
  };

  /* META TEMPORARILY DISABLED
  const loadMetaData = async (accessToken) => {
    try {
      const pages = await MetaAPIService.getPages(accessToken);
      const instagramAccounts = [];

      for (const page of pages) {
        const instagramAccount = await MetaAPIService.getInstagramAccounts(
          page.id,
          page.access_token,
        );
        if (instagramAccount) {
          instagramAccounts.push({
            pageId: page.id,
            pageName: page.name,
            instagramId: instagramAccount.id,
            accessToken: page.access_token,
          });
        }
      }

      setMetaData({ pages, instagramAccounts });
    } catch (error) {
      console.error("Error loading Meta data:", error);
    }
  };
  */

  const loadGoogleData = async (accessToken) => {
    try {
      const accounts = await GoogleAnalyticsService.getAccounts(accessToken);
      const properties = [];

      for (const account of accounts) {
        const accountProperties = await GoogleAnalyticsService.getProperties(
          account.name,
          accessToken,
        );
        properties.push(...accountProperties);
      }

      setGoogleData({ accounts, properties });
    } catch (error) {
      console.error("Error loading Google data:", error);
    }
  };

  const saveConnection = async (platform, data) => {
    try {
      const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(
        /\/+$/,
        "",
      );
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/api/connections`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          companyId: company?.id,
          platform,
          data,
        }),
      });

      if (!response.ok) {
        console.warn("Could not save connection to backend");
      }
    } catch (error) {
      console.error("Error saving connection:", error);
    }
  };

  const removeConnection = async (platform) => {
    try {
      const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(
        /\/+$/,
        "",
      );
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/api/connections/${platform}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          companyId: company?.id,
        }),
      });

      if (!response.ok) {
        console.warn("Could not remove connection from backend");
      }
    } catch (error) {
      console.error("Error removing connection:", error);
    }
  };

  const refreshConnection = async (platform) => {
    /* META TEMPORARILY DISABLED
    if (platform === "meta") {
      await checkExistingConnections();
    } else */ if (platform === "google") {
      const token = localStorage.getItem("google_analytics_token");
      if (token) {
        await loadGoogleData(token);
      }
    }
  };

  const ConnectionCard = ({
    title,
    subtitle,
    icons,
    connection,
    data,
    onConnectAction,
    onDisconnect,
    onRefresh,
    connectLabel,
    accentColor,
  }) => (
    <div
      className={`rounded-xl p-5 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center gap-1 ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
          >
            {icons}
          </div>
          <div>
            <h3
              className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
            >
              {title}
            </h3>
            <p
              className={`text-[12px] ${isDark ? "text-gray-600" : "text-gray-500"}`}
            >
              {subtitle}
            </p>
          </div>
        </div>

        <span
          className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full ${
            connection.connected
              ? isDark
                ? "bg-emerald-500/15 text-emerald-400"
                : "bg-emerald-50 text-emerald-700"
              : isDark
                ? "bg-white/[0.04] text-gray-500"
                : "bg-gray-100 text-gray-500"
          }`}
        >
          {connection.connected ? (
            <CheckCircle className="h-3 w-3" />
          ) : (
            <XCircle className="h-3 w-3" />
          )}
          {connection.connected ? "Verbonden" : "Niet verbonden"}
        </span>
      </div>

      {/* Error */}
      {connection.error && (
        <div
          className={`flex items-start gap-2 px-3 py-2.5 rounded-lg mb-4 text-[12px] ${isDark ? "bg-red-500/10 border border-red-500/20 text-red-400" : "bg-red-50 border border-red-200 text-red-700"}`}
        >
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <span>{connection.error}</span>
        </div>
      )}

      {/* Connected state */}
      {connection.connected ? (
        <div className="space-y-3">
          {data && (
            <div
              className={`rounded-lg p-3 space-y-1.5 ${isDark ? "bg-white/[0.02] border border-white/[0.04]" : "bg-gray-50 border border-gray-100"}`}
            >
              {data.pages && (
                <div className="flex justify-between items-center">
                  <span
                    className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Facebook Pagina's
                  </span>
                  <span
                    className={`text-[13px] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    {data.pages.length}
                  </span>
                </div>
              )}
              {data.instagramAccounts && (
                <div className="flex justify-between items-center">
                  <span
                    className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Instagram Accounts
                  </span>
                  <span
                    className={`text-[13px] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    {data.instagramAccounts.length}
                  </span>
                </div>
              )}
              {data.accounts && (
                <div className="flex justify-between items-center">
                  <span
                    className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Accounts
                  </span>
                  <span
                    className={`text-[13px] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    {data.accounts.length}
                  </span>
                </div>
              )}
              {data.properties && (
                <div className="flex justify-between items-center">
                  <span
                    className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Properties
                  </span>
                  <span
                    className={`text-[13px] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    {data.properties.length}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={onRefresh}
              disabled={connection.loading}
              className={`h-8 px-3 rounded-lg text-[12px] font-medium inline-flex items-center gap-1.5 transition-colors ${isDark ? "border border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border border-gray-200 text-gray-600 hover:bg-gray-50"}`}
            >
              <RefreshCw
                className={`h-3 w-3 ${connection.loading ? "animate-spin" : ""}`}
              />
              Vernieuwen
            </button>
            <button
              onClick={onDisconnect}
              className={`h-8 px-3 rounded-lg text-[12px] font-medium inline-flex items-center gap-1.5 transition-colors ${isDark ? "border border-red-500/20 text-red-400 hover:bg-red-500/10" : "border border-red-200 text-red-600 hover:bg-red-50"}`}
            >
              <Unlink className="h-3 w-3" />
              Verbreken
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={onConnectAction}
          disabled={connection.loading}
          className={`w-full h-10 rounded-lg text-[13px] font-medium inline-flex items-center justify-center gap-2 transition-colors ${accentColor}`}
        >
          {connection.loading ? (
            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <ExternalLink className="h-3.5 w-3.5" />
          )}
          {connection.loading ? "Verbinden..." : connectLabel}
        </button>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        <div className="mb-4">
          <h2
            className={`text-[15px] font-semibold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
          >
            Social Media Verbindingen
          </h2>
          <p
            className={`text-[13px] mt-0.5 ${isDark ? "text-gray-600" : "text-gray-500"}`}
          >
            Verbind uw social media accounts om statistieken te bekijken
          </p>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 gap-3">
        {/* META TEMPORARILY DISABLED
        <ConnectionCard
          title="Meta Platforms"
          subtitle="Facebook & Instagram"
          icons={
            <>
              <Facebook className="h-4 w-4 text-blue-500" />
              <Instagram className="h-4 w-4 text-pink-500" />
            </>
          }
          connection={connections.meta}
          data={metaData}
          onConnectAction={connectMeta}
          onDisconnect={disconnectMeta}
          onRefresh={() => refreshConnection("meta")}
          connectLabel="Verbinden met Meta"
          accentColor={
            isDark
              ? "bg-blue-600 text-white hover:bg-blue-500"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }
        />
        */}

        {/* Google Analytics Connection */}
        <ConnectionCard
          title="Google Analytics"
          subtitle="Website statistieken"
          icons={<BarChart3 className="h-4 w-4 text-emerald-500" />}
          connection={connections.google}
          data={googleData}
          onConnectAction={connectGoogle}
          onDisconnect={disconnectGoogle}
          onRefresh={() => refreshConnection("google")}
          connectLabel="Verbinden met Google Analytics"
          accentColor={
            isDark
              ? "bg-emerald-600 text-white hover:bg-emerald-500"
              : "bg-emerald-600 text-white hover:bg-emerald-700"
          }
        />
      </div>

      {/* Connection summary */}
      {
        /* connections.meta.connected || */ connections.google.connected && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.1 }}
          >
            <div
              className={`flex items-center gap-3 px-4 py-3 rounded-xl ${isDark ? "bg-emerald-500/10 border border-emerald-500/20" : "bg-emerald-50 border border-emerald-200"}`}
            >
              <CheckCircle
                className={`h-4 w-4 flex-shrink-0 ${isDark ? "text-emerald-400" : "text-emerald-600"}`}
              />
              <div>
                <h3
                  className={`text-[13px] font-medium ${isDark ? "text-emerald-300" : "text-emerald-800"}`}
                >
                  Verbonden accounts
                </h3>
                <p
                  className={`text-[12px] ${isDark ? "text-emerald-400/70" : "text-emerald-600"}`}
                >
                  U kunt nu statistieken bekijken van uw verbonden accounts
                </p>
              </div>
            </div>
          </motion.div>
        )
      }
    </div>
  );
};

export default SocialMediaConnect;
